'use client'

import React, { ForwardedRef, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { cva, VariantProps } from 'class-variance-authority'

import { cn } from '../../utils/cn'
import { InfoIcon, MinusIcon, PlusIcon } from '../icons'

import './input.css'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  /** Accessible label that appears above the input (required if no aria-label prop is present) */
  label?: string
  /** Displays this message below the input in gray (if there is no errorMessage) */
  hint?: string
  /** sets input as invalid */
  invalid?: boolean
  /** Displays this message in red below the input */
  errorMessage?: string

  /** removes padding if no error/hint is present */
  dense?: boolean
  color?: 'white' | 'gray'
  tooltipText?: string
}

export const inputColor = (
  color: 'white' | 'gray' | undefined,
  invalid: boolean | undefined
) => {
  if (invalid) return 'bg-error-50'

  switch (color) {
    case 'white':
      return 'bg-white'
    case 'gray':
      return 'bg-neutral-50'
    default:
      return undefined
  }
}
export const inputVariants = cva(
  'flex rounded-md border bg-transparent py-2 px-3 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-200 dark:border-slate-700 dark:text-slate-50 dark:focus:ring-slate-400 dark:focus:ring-offset-slate-900',
  {
    variants: {
      variantSize: {
        default: 'h-12 w-full',
        xs: 'h-7',
      },
    },
    defaultVariants: {
      variantSize: 'default',
    },
  }
)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      hint,
      invalid,
      errorMessage,
      variantSize,
      color,
      dense,
      tooltipText,
      ...props
    },
    ref
  ) => {
    const [uid] = useState<string>(React.useId())
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const inputRef = useForwardRef<HTMLInputElement>(ref)

    useEffect(() => {
      if (!props['aria-label'] && !label && !props['aria-labelledby']) {
        throw new Error(
          'Input elements require a label, aria-label, or aria-labelledby for accessibility.'
        )
      }
    }, [label, props])

    const renderDescription = () => {
      if (errorMessage && invalid) {
        return (
          <span
            id={`message-${uid}`}
            role="alert"
            className="text-error-800 text-xs"
            data-testid={'error-message-' + (label || props['aria-label'])}
          >
            {errorMessage}
          </span>
        )
      }
      if (hint) {
        return (
          <span
            id={`message-${uid}`}
            className="text-xs"
            data-testid={'hint-' + (label || props['aria-label'])}
          >
            {hint}
          </span>
        )
      }
      if (!dense) {
        return (
          <span role="presentation" style={{ visibility: 'hidden' }}>
            ...
          </span>
        )
      }
    }

    const numberBtnClick = (dir: 'up' | 'down') => {
      dir == 'up' ? inputRef.current.stepUp() : inputRef.current.stepDown()

      let customEvent = new Event('input', {
        bubbles: true,
      })
      inputRef.current.dispatchEvent(customEvent)
    }

    function renderTooltip() {
      if (!tooltipText) return

      const Tooltip = dynamic(() => import('../tooltip'))

      return (
        <Tooltip
          onOpenChange={(o) => setTooltipOpen(o)}
          open={tooltipOpen}
          trigger={
            <button
              aria-label="more information"
              onClick={() => setTooltipOpen(true)}
              type="button"
            >
              <InfoIcon size={16} className="fill-neutral-800" stroke="white" />
            </button>
          }
        >
          {tooltipText}
        </Tooltip>
      )
    }

    return (
      <div className={'base-input'}>
        {label && (
          <label
            data-testid={'label-' + (label || props['aria-label'])}
            htmlFor={`input-${uid}`}
            className={cn(
              invalid && 'text-error-800',
              'font-semibold text-sm',
              'flex items-center gap-1 mb-1'
            )}
          >
            {label} {renderTooltip()}
          </label>
        )}

        <div
          className={cn(props.type === 'number' && 'flex items-center gap-2')}
        >
          {props.type === 'number' && (
            <div>
              <button
                aria-label="decrease"
                data-testid="decrease-button"
                aria-describedby={`input-${uid}`}
                className={cn(
                  'number-btn',
                  variantSize || 'default',
                  'hover:bg-neutral-100 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed'
                )}
                onClick={(e) => {
                  e.preventDefault()
                  numberBtnClick('down')
                }}
                disabled={props.disabled}
              >
                <MinusIcon width={30} className="fill-neutral-900" />
              </button>
            </div>
          )}
          <input
            data-testid={'input-' + (label || props['aria-label'])}
            id={`input-${uid}`}
            aria-invalid={invalid}
            aria-describedby={`message-${uid}`}
            className={cn(
              invalid
                ? 'border-error-800'
                : 'border-neutral-400 hover:border-neutral-500',
              inputVariants({ variantSize }),
              variantSize || 'default',
              className,
              inputColor(color, invalid)
            )}
            ref={inputRef}
            {...props}
          />

          {props.type === 'number' && (
            <div>
              <button
                aria-label="increase"
                data-testid="increase-button"
                aria-describedby={`input-${uid}`}
                className={cn(
                  'number-btn',
                  variantSize || 'default',
                  'hover:bg-neutral-100 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed'
                )}
                onClick={(e) => {
                  e.preventDefault()
                  numberBtnClick('up')
                }}
                disabled={props.disabled}
              >
                <PlusIcon className="fill-neutral-900" />
              </button>
            </div>
          )}
        </div>

        {renderDescription()}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }

const useForwardRef = <T,>(ref: ForwardedRef<T>, initialValue: any = null) => {
  const targetRef = useRef<T>(initialValue)

  useEffect(() => {
    if (!ref) return

    if (typeof ref === 'function') {
      ref(targetRef.current)
    } else {
      ref.current = targetRef.current
    }
  }, [ref])

  return targetRef
}
